import { useState } from "react";
import { Plus, X, Edit3, Check, ClipboardList, Activity, Target, Eye } from "lucide-react";

/**
 * Inline editor for one of the three Clinical Snapshot lists.
 * Shows items as chips; in edit mode reveals an add/remove UI.
 */
function ListEditor({ icon: Icon, label, hint, items, onChange, accent, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const accentClasses = accent === "terracotta"
    ? { chip: "bg-terracotta-500/10 text-terracotta-600", icon: "text-terracotta-500" }
    : accent === "sage"
    ? { chip: "bg-sage-500/10 text-sage-700", icon: "text-sage-600" }
    : accent === "amber"
    ? { chip: "bg-amber-100 text-amber-700", icon: "text-amber-600" }
    : { chip: "bg-paper-100 text-ink-700", icon: "text-ink-700" };

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (items.includes(t)) { setDraft(""); return; }
    onChange([...items, t]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5" data-testid={`snapshot-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accentClasses.icon}`} strokeWidth={1.75} />
          <div className="label-overline">{label}</div>
        </div>
        <button
          onClick={() => setEditing((v) => !v)}
          className="text-ink-500 hover:text-ink-900 text-xs inline-flex items-center gap-1"
          data-testid={`snapshot-${testId}-edit-toggle`}
        >
          {editing ? <><Check className="w-3.5 h-3.5" /> Done</> : <><Edit3 className="w-3.5 h-3.5" /> Edit</>}
        </button>
      </div>
      {!editing && (
        items.length === 0 ? (
          <div className="text-sm text-ink-500 italic">None recorded yet — tap Edit to add.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((item, i) => (
              <span key={`${testId}-${i}-${item.slice(0,16)}`} className={`px-3 py-1.5 rounded-full text-sm ${accentClasses.chip}`}>
                {item}
              </span>
            ))}
          </div>
        )
      )}
      {editing && (
        <div className="space-y-2">
          <ul className="space-y-2">
            {items.map((item, i) => (
              <li key={`${testId}-edit-${i}-${item.slice(0,16)}`} className="flex items-start gap-2 bg-paper-50 rounded-xl px-3 py-2">
                <span className="text-sm text-ink-900 flex-1">{item}</span>
                <button onClick={() => remove(i)} className="text-ink-500 hover:text-destructive" aria-label="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <input
              data-testid={`snapshot-${testId}-input`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder={hint}
              className="flex-1 min-h-[40px] px-3 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
            />
            <button onClick={add} className="w-10 h-10 rounded-full bg-paper-100 hover:bg-paper-200 text-ink-700 flex items-center justify-center" aria-label="Add" data-testid={`snapshot-${testId}-add`}>
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Persistent Clinical Snapshot — three editable lists that follow a client across every session.
 * AI sees these on every assessment / SOAP draft / wrap-up.
 */
export default function ClinicalSnapshotEditor({ client, onSave }) {
  // Local state so edits feel snappy; debounce-save on each change
  const [medicalHistory, setMedicalHistory] = useState(client.medical_history || []);
  const [clinicalPatterns, setClinicalPatterns] = useState(client.clinical_patterns || []);
  const [clinicalFindings, setClinicalFindings] = useState(client.clinical_findings || []);
  const [longTermGoals, setLongTermGoals] = useState(client.long_term_goals || []);

  const persist = (patch) => onSave?.(patch);

  return (
    <div className="bg-paper-50 border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid="clinical-snapshot">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs uppercase tracking-[0.14em]">
            Persistent clinical snapshot
          </div>
          <h2 className="font-heading text-xl text-ink-900 mt-2">Things to always remember about {client.name}</h2>
        </div>
        <p className="text-xs text-ink-500 max-w-xs text-right">
          The AI sees this on every assessment, SOAP draft, and wrap-up. Add anything that matters across visits.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <ListEditor
          icon={ClipboardList}
          label="Medical history"
          hint="e.g. Right femur rod, HLA-B27 positive"
          items={medicalHistory}
          onChange={(v) => { setMedicalHistory(v); persist({ medical_history: v }); }}
          accent="terracotta"
          testId="medical-history"
        />
        <ListEditor
          icon={Activity}
          label="Clinical patterns"
          hint="e.g. Recurring upper trap hypertonicity"
          items={clinicalPatterns}
          onChange={(v) => { setClinicalPatterns(v); persist({ clinical_patterns: v }); }}
          accent="sage"
          testId="clinical-patterns"
        />
        <ListEditor
          icon={Eye}
          label="Therapist clinical findings"
          hint="e.g. Forward head posture worsens on Fri visits; chronic R QL guarding"
          items={clinicalFindings}
          onChange={(v) => { setClinicalFindings(v); persist({ clinical_findings: v }); }}
          accent="amber"
          testId="clinical-findings"
        />
        <ListEditor
          icon={Target}
          label="Long-term goals"
          hint="e.g. Restore full cervical rotation"
          items={longTermGoals}
          onChange={(v) => { setLongTermGoals(v); persist({ long_term_goals: v }); }}
          accent="neutral"
          testId="long-term-goals"
        />
      </div>
    </div>
  );
}
